import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  InboxMessage,
  InboxSummary,
  getInboxConfig,
  syncInbox,
  listInboxMessages,
  getInboxSummary,
  markInboxMessageSeen,
  markAllInboxSeen,
  draftInboxReply,
  QuestAsk,
  QuestAskByQuest,
  listQuestAsks,
  syncQuestAsks,
  markQuestAskSeen,
  markAllQuestAsksSeen,
  updateQuestAskState,
} from '../services/api';

// ============================================================================
// Responses: the "sign that a campaign/outreach reply, a mail in my own inbox,
// or feedback Joshua left on a Quest email has arrived" page. Read + mark-seen
// (and, for Quest asks, act-on) only -- no send/approve actions live here. The
// cockpit reads the shared support@spiritualdata.org inbox (where campaign
// replies land via reply-to) plus every team member's own personal mailbox
// (2026-09-11: "I want every account to have its privacy" -- the backend scopes
// every /api/inbox/* response to mailboxes the SIGNED-IN caller may see, so this
// page only ever shows your own mail plus the shared inbox, automatically, no
// matter what filters are picked here). Joshua runs multiple campaigns at once,
// so the "Responses by campaign" strip is the at-a-glance answer to "which of my
// campaigns got new replies."
//
// Quest asks (added 2026-09-14): Joshua's feedback on team work arrives as a
// reply to a Quest email, and Quest tracks it as an "ask." Zee works in this
// cockpit, not the Quest app, so that feedback was invisible where he actually
// works -- this tab brings Quest asks in as a first-class source, defaulted to
// "still owed" (nobody has finished it yet) since that is the actual signal.
// ============================================================================

const PAGE_SIZE = 50;

// The known Quest-ask state vocabulary, for the filter and per-ask state menus. Quest may grow
// this list server-side; an ask returning a value outside this set still displays fine as plain text.
const QUEST_ASK_STATES = [
  'open',
  'in progress',
  'partially applied',
  'needs reapplying',
  'done',
  'in force',
  'declined',
  'noted',
];

const kindLabel = (kind: string): string => {
  if (!kind) return 'Unknown';
  return kind.charAt(0).toUpperCase() + kind.slice(1);
};

const fmtTime = (iso: string | null): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const errMsg = (err: any, fallback: string): string => err?.response?.data?.detail || fallback;

// Decrement a summary in place (all-new-object, immutable) after one message is marked seen,
// so the badge/strip react instantly without waiting for the next poll.
const decrementUnseen = (s: InboxSummary, m: InboxMessage): InboxSummary => {
  const next: InboxSummary = {
    ...s,
    total_unseen: Math.max(0, s.total_unseen - 1),
    by_mailbox: { ...s.by_mailbox },
    by_campaign: { ...s.by_campaign },
  };
  if (m.mailbox && next.by_mailbox[m.mailbox]) {
    next.by_mailbox[m.mailbox] = {
      ...next.by_mailbox[m.mailbox],
      unseen: Math.max(0, next.by_mailbox[m.mailbox].unseen - 1),
    };
  }
  if (m.campaign_id != null) {
    const key = String(m.campaign_id);
    if (next.by_campaign[key]) {
      next.by_campaign[key] = {
        ...next.by_campaign[key],
        unseen: Math.max(0, next.by_campaign[key].unseen - 1),
      };
    }
  }
  if (m.campaign_id == null && m.email_draft_id != null) {
    next.draft_reply_unseen = Math.max(0, next.draft_reply_unseen - 1);
  } else if (!m.campaign_id && (m.match_method === 'unmatched' || !m.match_method)) {
    next.unmatched_unseen = Math.max(0, next.unmatched_unseen - 1);
  }
  return next;
};

// A live snapshot for the Quest-asks signal strip: total unseen plus the per-quest breakdown.
// Kept as one object (mirroring InboxSummary above) so a single functional setState call can
// update both atomically after a mark-seen.
type QuestAskSummary = { unseen: number; by_quest: QuestAskByQuest };

// Decrement the Quest-asks signal strip in place after one ask is marked seen, so the badge and
// per-quest breakdown react instantly without waiting for the next poll.
const decrementQuestUnseen = (s: QuestAskSummary, a: QuestAsk): QuestAskSummary => {
  const next: QuestAskSummary = { unseen: Math.max(0, s.unseen - 1), by_quest: { ...s.by_quest } };
  if (next.by_quest[a.quest_id]) {
    next.by_quest[a.quest_id] = {
      ...next.by_quest[a.quest_id],
      unseen: Math.max(0, next.by_quest[a.quest_id].unseen - 1),
    };
  }
  return next;
};

// The shared support inbox gets its own color; every personal mailbox (everyone's own, now that
// the whole team is monitored) shares a second color so a new team member's mailbox degrades to
// a plain, already-styled pill instead of breaking.
const mailboxColor = (theme: any, mailbox: string | null): string => {
  if (!mailbox) return theme.textSecondary;
  if (mailbox.startsWith('support@')) return theme.info;
  return theme.primary;
};

const Responses: React.FC = () => {
  const { theme } = useTheme();

  const [tab, setTab] = useState<'email' | 'quest'>('email');

  const [config, setConfig] = useState<{ configured: boolean; reason: string | null } | null>(null);
  const [summary, setSummary] = useState<InboxSummary | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  // Filters
  const [mailboxFilter, setMailboxFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [unseenOnly, setUnseenOnly] = useState(false);
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [draftReplyOnly, setDraftReplyOnly] = useState(false);

  // Messages + pagination
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesRef = useRef<InboxMessage[]>([]);
  messagesRef.current = messages;

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [draftingReplyId, setDraftingReplyId] = useState<number | null>(null);
  const [draftReplyError, setDraftReplyError] = useState<string | null>(null);

  // Quest asks: filters
  const [questIdFilter, setQuestIdFilter] = useState('');
  const [questStateFilter, setQuestStateFilter] = useState('');
  // "Still owed" is the default view -- an ask nobody has finished is the signal this tab exists
  // to surface; done/in force asks are history you opt into, not the default.
  const [stillOwedOnly, setStillOwedOnly] = useState(true);
  const [questUnseenOnly, setQuestUnseenOnly] = useState(false);

  // Quest asks: list + pagination
  const [questAsks, setQuestAsks] = useState<QuestAsk[]>([]);
  const [questTotal, setQuestTotal] = useState(0);
  const [questSummary, setQuestSummary] = useState<QuestAskSummary | null>(null);
  const [loadingQuestList, setLoadingQuestList] = useState(false);
  const [loadingMoreQuest, setLoadingMoreQuest] = useState(false);
  const questAsksRef = useRef<QuestAsk[]>([]);
  questAsksRef.current = questAsks;

  const [expandedQuestKey, setExpandedQuestKey] = useState<string | null>(null);
  const [questBanner, setQuestBanner] = useState<string | null>(null);
  const [questSyncing, setQuestSyncing] = useState(false);
  const [questSyncResult, setQuestSyncResult] = useState<string | null>(null);
  const [questMarkingAll, setQuestMarkingAll] = useState(false);

  // Per-ask state-change draft (state select + optional note), keyed by ask key so each expanded
  // row keeps its own in-progress edit.
  const [stateDraft, setStateDraft] = useState<Record<string, string>>({});
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  useEffect(() => {
    getInboxConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  const loadSummary = useCallback(async (silent = false) => {
    try {
      const res = await getInboxSummary();
      setSummary(res);
    } catch (err: any) {
      if (!silent) setBanner(errMsg(err, 'Could not load the response summary.'));
    }
  }, []);

  // Server-side filter params shared by every list fetch (unseenOnly is applied
  // client-side below since the API has no such param).
  const serverParams = useMemo(
    () => ({
      mailbox: mailboxFilter || undefined,
      campaign_id: campaignFilter ? Number(campaignFilter) : undefined,
      unmatched_only: unmatchedOnly || undefined,
      draft_reply_only: draftReplyOnly || undefined,
    }),
    [mailboxFilter, campaignFilter, unmatchedOnly, draftReplyOnly]
  );

  // Full reset to page 1: first load and whenever a filter changes.
  const loadMessages = useCallback(
    async (silent = false) => {
      if (!silent) setLoadingList(true);
      try {
        const res = await listInboxMessages({ ...serverParams, limit: PAGE_SIZE, offset: 0 });
        setMessages(res.messages);
        setTotalCount(res.count);
      } catch (err: any) {
        if (!silent) setBanner(errMsg(err, 'Could not load messages.'));
      } finally {
        if (!silent) setLoadingList(false);
      }
    },
    [serverParams]
  );

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Silent poll refresh: re-fetch exactly the window already loaded (not reset to page 1's
  // size), so the periodic tick never undoes "Load more" or collapses an expanded row
  // (expand state is keyed by message id, not array index).
  const refreshWindow = useCallback(async () => {
    try {
      const currentSize = Math.max(messagesRef.current.length, PAGE_SIZE);
      const res = await listInboxMessages({ ...serverParams, limit: currentSize, offset: 0 });
      setMessages(res.messages);
      setTotalCount(res.count);
    } catch {
      // signal-only poll; a miss just leaves the stale list until the next tick
    }
  }, [serverParams]);

  // Quest asks: an unfiltered snapshot for the signal strip (unseen total + per-quest breakdown),
  // independent of whatever filters are picked below -- mirrors loadSummary/getInboxSummary above.
  const loadQuestSummary = useCallback(async (silent = false) => {
    try {
      const res = await listQuestAsks({});
      setQuestSummary({ unseen: res.unseen, by_quest: res.by_quest });
    } catch (err: any) {
      if (!silent) setQuestBanner(errMsg(err, 'Could not load the Quest asks summary.'));
    }
  }, []);

  // Quest asks: server-side filter params shared by every list fetch.
  const questServerParams = useMemo(
    () => ({
      quest_id: questIdFilter || undefined,
      state: questStateFilter || undefined,
      still_owed_only: stillOwedOnly || undefined,
      unseen_only: questUnseenOnly || undefined,
    }),
    [questIdFilter, questStateFilter, stillOwedOnly, questUnseenOnly]
  );

  // Full reset to page 1: first load and whenever a Quest-asks filter changes.
  const loadQuestAsks = useCallback(
    async (silent = false) => {
      if (!silent) setLoadingQuestList(true);
      try {
        const res = await listQuestAsks({ ...questServerParams, limit: PAGE_SIZE, offset: 0 });
        setQuestAsks(res.items);
        setQuestTotal(res.total);
      } catch (err: any) {
        if (!silent) setQuestBanner(errMsg(err, 'Could not load Quest asks.'));
      } finally {
        if (!silent) setLoadingQuestList(false);
      }
    },
    [questServerParams]
  );

  useEffect(() => {
    loadQuestAsks();
  }, [loadQuestAsks]);

  // Silent poll refresh for Quest asks: re-fetch exactly the window already loaded, same reasoning
  // as refreshWindow above (never undo "Load more" or collapse an expanded row mid-poll).
  const refreshQuestWindow = useCallback(async () => {
    try {
      const currentSize = Math.max(questAsksRef.current.length, PAGE_SIZE);
      const res = await listQuestAsks({ ...questServerParams, limit: currentSize, offset: 0 });
      setQuestAsks(res.items);
      setQuestTotal(res.total);
    } catch {
      // signal-only poll; a miss just leaves the stale list until the next tick
    }
  }, [questServerParams]);

  // Silent 12s-while-visible poll, matching Campaigns.tsx. Only re-fetches already-synced
  // data (messages + summary, and now Quest asks + their summary): never triggers
  // /inbox/sync or /quest-asks/sync, which hit real external services and are cron-driven
  // server-side. Quest asks share this single timer rather than adding a second one.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadSummary(true);
        refreshWindow();
        loadQuestSummary(true);
        refreshQuestWindow();
      }
    }, 12000);
    return () => window.clearInterval(id);
  }, [loadSummary, refreshWindow, loadQuestSummary, refreshQuestWindow]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadQuestSummary();
  }, [loadQuestSummary]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await listInboxMessages({ ...serverParams, limit: PAGE_SIZE, offset: messages.length });
      setMessages((prev) => [...prev, ...res.messages]);
      setTotalCount(res.count);
    } catch (err: any) {
      setBanner(errMsg(err, 'Could not load more messages.'));
    } finally {
      setLoadingMore(false);
    }
  };

  const doSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setBanner(null);
    try {
      const res = await syncInbox();
      if (!res.configured) {
        setSyncResult('Inbox sync is not configured.');
      } else if (res.new > 0) {
        setSyncResult(
          `Pulled ${res.new} new message${res.new === 1 ? '' : 's'} (${res.fetched} fetched, ${res.matched} matched to a campaign).`
        );
      } else {
        setSyncResult('Nothing new.');
      }
      await Promise.all([loadSummary(true), loadMessages(true)]);
    } catch (err: any) {
      setSyncResult(errMsg(err, 'Could not sync the inbox.'));
    } finally {
      setSyncing(false);
    }
  };

  const doMarkAllSeen = async () => {
    setMarkingAll(true);
    setBanner(null);
    try {
      await markAllInboxSeen({
        mailbox: mailboxFilter || undefined,
        campaign_id: campaignFilter ? Number(campaignFilter) : undefined,
      });
      await Promise.all([loadSummary(true), loadMessages(true)]);
    } catch (err: any) {
      setBanner(errMsg(err, 'Could not mark messages as seen.'));
    } finally {
      setMarkingAll(false);
    }
  };

  const toggleExpand = async (m: InboxMessage) => {
    const next = expandedId === m.id ? null : m.id;
    setExpandedId(next);
    if (next !== null && !m.seen_at) {
      try {
        await markInboxMessageSeen(m.id);
        const nowIso = new Date().toISOString();
        setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, seen_at: nowIso } : x)));
        setSummary((prev) => (prev ? decrementUnseen(prev, m) : prev));
      } catch {
        // best effort; a failed seen-mark just leaves the row bold until the next sync
      }
    }
  };

  const loadMoreQuest = async () => {
    setLoadingMoreQuest(true);
    try {
      const res = await listQuestAsks({ ...questServerParams, limit: PAGE_SIZE, offset: questAsks.length });
      setQuestAsks((prev) => [...prev, ...res.items]);
      setQuestTotal(res.total);
    } catch (err: any) {
      setQuestBanner(errMsg(err, 'Could not load more Quest asks.'));
    } finally {
      setLoadingMoreQuest(false);
    }
  };

  const doQuestSync = async () => {
    setQuestSyncing(true);
    setQuestSyncResult(null);
    setQuestBanner(null);
    try {
      const res = await syncQuestAsks();
      if (res.new > 0 || res.updated > 0) {
        setQuestSyncResult(`Pulled ${res.new} new ask${res.new === 1 ? '' : 's'}, updated ${res.updated}.`);
      } else {
        setQuestSyncResult('Nothing new.');
      }
      await Promise.all([loadQuestSummary(true), loadQuestAsks(true)]);
    } catch (err: any) {
      setQuestSyncResult(errMsg(err, 'Could not sync Quest asks.'));
    } finally {
      setQuestSyncing(false);
    }
  };

  const doMarkAllQuestSeen = async () => {
    setQuestMarkingAll(true);
    setQuestBanner(null);
    try {
      await markAllQuestAsksSeen();
      await Promise.all([loadQuestSummary(true), loadQuestAsks(true)]);
    } catch (err: any) {
      setQuestBanner(errMsg(err, 'Could not mark Quest asks as seen.'));
    } finally {
      setQuestMarkingAll(false);
    }
  };

  const toggleExpandQuest = async (a: QuestAsk) => {
    const next = expandedQuestKey === a.key ? null : a.key;
    setExpandedQuestKey(next);
    if (next !== null) {
      setStateDraft((prev) => (prev[a.key] !== undefined ? prev : { ...prev, [a.key]: a.state }));
      if (!a.seen_at) {
        try {
          await markQuestAskSeen(a.key);
          const nowIso = new Date().toISOString();
          setQuestAsks((prev) => prev.map((x) => (x.key === a.key ? { ...x, seen_at: nowIso } : x)));
          setQuestSummary((prev) => (prev ? decrementQuestUnseen(prev, a) : prev));
        } catch {
          // best effort; a failed seen-mark just leaves the row bold until the next sync
        }
      }
    }
  };

  // Sets an ask's state (at minimum, marking it done) with an optional note -- this is what turns
  // the ask list from a feed into a worklist. The PATCH response is the source of truth for the row.
  const doUpdateAskState = async (a: QuestAsk) => {
    const state = stateDraft[a.key] ?? a.state;
    const note = (noteDraft[a.key] || '').trim();
    setUpdatingKey(a.key);
    setUpdateError(null);
    try {
      const updated = await updateQuestAskState(a.key, note ? { state, note } : { state });
      setQuestAsks((prev) => prev.map((x) => (x.key === a.key ? updated : x)));
      setStateDraft((prev) => ({ ...prev, [a.key]: updated.state }));
      setNoteDraft((prev) => ({ ...prev, [a.key]: '' }));
      // A state change can move an ask off the current "still owed" / state filter -- refresh the
      // signal strip so the badge and per-quest counts stay honest.
      loadQuestSummary(true);
    } catch (err: any) {
      setUpdateError(errMsg(err, 'Could not update this ask.'));
    } finally {
      setUpdatingKey(null);
    }
  };

  // AI-drafts a reply into the Approval Queue -- NEVER sends anything itself. Idempotent server
  // side, so a second click for the same message just re-fetches the draft it already made.
  const doDraftReply = async (m: InboxMessage) => {
    setDraftingReplyId(m.id);
    setDraftReplyError(null);
    try {
      const result = await draftInboxReply(m.id);
      if (result.error) {
        setDraftReplyError(result.error);
        return;
      }
      const draftId = result.draft?.id;
      if (draftId) {
        setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, reply_draft_id: draftId } : x)));
      }
    } catch (err: any) {
      setDraftReplyError(errMsg(err, 'Could not draft a reply.'));
    } finally {
      setDraftingReplyId(null);
    }
  };

  const mailboxOptions = useMemo(() => {
    const set = new Set<string>();
    if (summary) Object.keys(summary.by_mailbox).forEach((m) => set.add(m));
    messages.forEach((m) => {
      if (m.mailbox) set.add(m.mailbox);
    });
    return Array.from(set).sort();
  }, [summary, messages]);

  // Campaigns sorted so the ones with new responses surface first — the whole point of this
  // page when several campaigns are running at once.
  const campaignEntries = useMemo(() => {
    if (!summary) return [] as [string, { unseen: number; total: number; campaign_name: string | null }][];
    return Object.entries(summary.by_campaign).sort((a, b) => {
      if (b[1].unseen !== a[1].unseen) return b[1].unseen - a[1].unseen;
      return b[1].total - a[1].total;
    });
  }, [summary]);

  const campaignLabel = (m: InboxMessage): string => {
    if (m.campaign_id != null) {
      const entry = summary?.by_campaign?.[String(m.campaign_id)];
      return entry?.campaign_name || `Campaign #${m.campaign_id}`;
    }
    if (m.email_draft_id != null) {
      // A reply to a single email sent from the Approval Queue, not a bulk campaign.
      const who = m.email_draft_person ? ` (${m.email_draft_person})` : '';
      return m.email_draft_subject
        ? `Reply to your email: "${m.email_draft_subject}"${who}`
        : `Reply to your email${who}`;
    }
    return 'General / unmatched';
  };

  const visibleMessages = unseenOnly ? messages.filter((m) => !m.seen_at) : messages;

  // Quests sorted so the ones with new asks surface first, same reasoning as campaignEntries above.
  const questFilterEntries = useMemo(() => {
    if (!questSummary) return [] as [string, { title: string; total: number; unseen: number }][];
    return Object.entries(questSummary.by_quest).sort((a, b) => {
      if (b[1].unseen !== a[1].unseen) return b[1].unseen - a[1].unseen;
      return b[1].total - a[1].total;
    });
  }, [questSummary]);

  // ---- styles ----------------------------------------------------------------
  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '14px',
  };
  const btn = (bg: string): React.CSSProperties => ({
    backgroundColor: bg,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '7px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  });
  const ghostBtn = (active: boolean): React.CSSProperties => ({
    backgroundColor: active ? theme.primary : 'transparent',
    color: active ? '#FFFFFF' : theme.textSecondary,
    border: `1px solid ${active ? theme.primary : theme.border}`,
    borderRadius: '7px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  });
  const selectStyle: React.CSSProperties = {
    backgroundColor: theme.background,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: '6px',
    padding: '6px 8px',
    fontSize: '13px',
  };
  const kindChipStyle: React.CSSProperties = {
    fontSize: '11px',
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: '999px',
    padding: '1px 8px',
  };
  const stateChipStyle = (stillOwed: boolean): React.CSSProperties => ({
    fontSize: '11px',
    fontWeight: stillOwed ? 700 : 400,
    color: stillOwed ? theme.warning : theme.textSecondary,
    border: `1px solid ${stillOwed ? theme.warning : theme.border}`,
    borderRadius: '999px',
    padding: '1px 8px',
  });
  const owedChipStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 700,
    color: '#FFFFFF',
    backgroundColor: theme.error,
    borderRadius: '999px',
    padding: '1px 8px',
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ color: theme.text, margin: 0 }}>{tab === 'email' ? 'Email Responses' : 'Quest Asks'}</h1>
      {tab === 'email' ? (
        <p style={{ color: theme.textSecondary, margin: '6px 0 0', fontSize: '14px', maxWidth: '760px' }}>
          Every reply that landed in the shared support@spiritualdata.org inbox (campaign and
          outreach replies) or your own personal mailbox, threaded back to the campaign or draft it
          answers where possible. This is the sign a response arrived, across every campaign running
          at once. You only ever see your own mail here, never anyone else's.
        </p>
      ) : (
        <p style={{ color: theme.textSecondary, margin: '6px 0 0', fontSize: '14px', maxWidth: '760px' }}>
          Feedback Joshua left as a reply to a Quest email, tracked in Quest as an ask. Zee works
          here, not in the Quest app, so this is where that feedback becomes visible and actionable.
          Still owed asks are what nobody has finished; done and in force asks are history.
        </p>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button onClick={() => setTab('email')} style={ghostBtn(tab === 'email')}>
          Email
        </button>
        <button onClick={() => setTab('quest')} style={ghostBtn(tab === 'quest')}>
          Quest asks{questSummary && questSummary.unseen > 0 ? ` (${questSummary.unseen})` : ''}
        </button>
      </div>

      {tab === 'email' && (
      <>
      {config && !config.configured && (
        <div
          style={{
            ...cardStyle,
            borderColor: theme.warning,
            marginTop: '16px',
            color: theme.text,
            fontSize: '13px',
          }}
        >
          <strong style={{ color: theme.warning }}>Inbox reading is not configured.</strong> {config.reason} No
          responses can be shown until this is resolved.
        </div>
      )}

      {/* Signal strip */}
      <div style={{ ...cardStyle, marginTop: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div>
            <div style={{ color: theme.text, fontSize: '36px', fontWeight: 700, lineHeight: 1 }}>
              {summary ? summary.total_unseen : '-'}
            </div>
            <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '4px' }}>unseen response{summary?.total_unseen === 1 ? '' : 's'}</div>
          </div>
          <div style={{ color: theme.textSecondary, fontSize: '12px' }}>
            {summary ? `${summary.total_messages} message${summary.total_messages === 1 ? '' : 's'} total` : ''}
            {summary && summary.draft_reply_unseen > 0
              ? ` · ${summary.draft_reply_unseen} unseen repl${summary.draft_reply_unseen === 1 ? 'y' : 'ies'} to your emails`
              : ''}
            {summary && summary.unmatched_unseen > 0 ? ` · ${summary.unmatched_unseen} unseen unmatched` : ''}
          </div>
        </div>

        {summary && Object.keys(summary.by_mailbox).length > 0 && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
            {Object.entries(summary.by_mailbox).map(([mailbox, counts]) => (
              <div
                key={mailbox}
                style={{
                  border: `1px solid ${mailboxColor(theme, mailbox)}`,
                  borderRadius: '8px',
                  padding: '8px 12px',
                  minWidth: '160px',
                }}
              >
                <div style={{ color: mailboxColor(theme, mailbox), fontSize: '12px', fontWeight: 600 }}>{mailbox}</div>
                <div style={{ color: theme.text, fontSize: '18px', fontWeight: 700, marginTop: '2px' }}>
                  {counts.unseen} <span style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 400 }}>unseen</span>
                </div>
                <div style={{ color: theme.textSecondary, fontSize: '11px' }}>{counts.total} total</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>
          Responses by campaign
        </div>
        {campaignEntries.length === 0 ? (
          <div style={{ color: theme.textSecondary, fontSize: '13px' }}>No campaign-linked responses yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {campaignEntries.map(([campaignId, counts]) => (
              <Link
                key={campaignId}
                to="/campaigns"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.border}`,
                  textDecoration: 'none',
                  backgroundColor: counts.unseen > 0 ? theme.background : 'transparent',
                }}
              >
                <span style={{ color: theme.text, fontSize: '13px', flex: '1 1 auto', fontWeight: counts.unseen > 0 ? 700 : 400 }}>
                  {counts.campaign_name || `Campaign #${campaignId}`}
                </span>
                {counts.unseen > 0 && (
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#FFFFFF',
                      backgroundColor: theme.error,
                      borderRadius: '999px',
                      padding: '1px 8px',
                    }}
                  >
                    {counts.unseen} new
                  </span>
                )}
                <span style={{ color: theme.textSecondary, fontSize: '12px' }}>{counts.total} total</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ ...cardStyle, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ color: theme.textSecondary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Mailbox
          <select value={mailboxFilter} onChange={(e) => setMailboxFilter(e.target.value)} style={selectStyle}>
            <option value="">All</option>
            {mailboxOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>

        <label style={{ color: theme.textSecondary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Campaign
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} style={selectStyle}>
            <option value="">All campaigns</option>
            {campaignEntries.map(([campaignId, counts]) => (
              <option key={campaignId} value={campaignId}>
                {counts.campaign_name || `Campaign #${campaignId}`}
              </option>
            ))}
          </select>
        </label>

        <button onClick={() => setUnseenOnly((v) => !v)} style={ghostBtn(unseenOnly)}>
          Unseen only
        </button>
        <button
          onClick={() => {
            setUnmatchedOnly((v) => !v);
            setDraftReplyOnly(false);
          }}
          style={ghostBtn(unmatchedOnly)}
        >
          Unmatched only
        </button>
        <button
          onClick={() => {
            setDraftReplyOnly((v) => !v);
            setUnmatchedOnly(false);
          }}
          style={ghostBtn(draftReplyOnly)}
        >
          Replies to my emails
        </button>

        <button onClick={doSync} disabled={syncing} style={{ ...btn(theme.primary), marginLeft: 'auto' }}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
        <button onClick={doMarkAllSeen} disabled={markingAll || (summary?.total_unseen ?? 0) === 0} style={ghostBtn(false)}>
          {markingAll ? 'Marking…' : 'Mark all as seen'}
        </button>
      </div>

      {syncResult && <div style={{ ...cardStyle, color: theme.text, fontSize: '13px' }}>{syncResult}</div>}
      {banner && <div style={{ ...cardStyle, color: theme.error, fontSize: '13px' }}>{banner}</div>}

      {/* Message list */}
      {loadingList && messages.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>Loading…</div>
      ) : visibleMessages.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>
          {messages.length === 0 ? 'No messages here yet.' : 'Nothing unseen at this filter.'}
        </div>
      ) : (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', overflow: 'hidden' }}>
          {visibleMessages.map((m, i) => {
            const unseen = !m.seen_at;
            const open = expandedId === m.id;
            return (
              <div
                key={m.id}
                style={{
                  borderTop: i === 0 ? 'none' : `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                }}
              >
                <div
                  onClick={() => toggleExpand(m)}
                  title="Click to view the full message"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    cursor: 'pointer',
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    title={unseen ? 'Unseen' : 'Seen'}
                    style={{
                      marginTop: '5px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '999px',
                      backgroundColor: unseen ? theme.error : theme.border,
                      flex: '0 0 auto',
                    }}
                  />
                  <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: mailboxColor(theme, m.mailbox),
                          border: `1px solid ${mailboxColor(theme, m.mailbox)}`,
                          borderRadius: '999px',
                          padding: '1px 8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.mailbox || 'unknown mailbox'}
                      </span>
                      <span
                        style={{
                          color: theme.text,
                          fontSize: '13px',
                          fontWeight: unseen ? 700 : 400,
                        }}
                      >
                        {m.from_name || m.from_addr}
                      </span>
                      <span style={{ color: theme.textSecondary, fontSize: '12px' }}>{m.from_addr}</span>
                      <span style={{ color: theme.textSecondary, fontSize: '12px', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                        {fmtTime(m.received_at)}
                      </span>
                    </div>
                    <div style={{ color: theme.text, fontSize: '13px', fontWeight: unseen ? 700 : 400, marginTop: '4px' }}>
                      {m.subject || '(no subject)'}
                    </div>
                    {m.snippet && !open && (
                      <div
                        style={{
                          color: theme.textSecondary,
                          fontSize: '12px',
                          marginTop: '2px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.snippet}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          color: m.campaign_id != null ? theme.info : theme.textSecondary,
                          border: `1px solid ${m.campaign_id != null ? theme.info : theme.border}`,
                          borderRadius: '999px',
                          padding: '1px 8px',
                        }}
                      >
                        {campaignLabel(m)}
                      </span>
                      {m.is_joshua_preview_reply && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: theme.warning,
                            border: `1px solid ${theme.warning}`,
                            borderRadius: '999px',
                            padding: '1px 8px',
                          }}
                        >
                          Preview reply{m.preview_go_detected ? ' · GO detected' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={{ color: theme.textSecondary, fontSize: '12px' }}>{open ? '▲' : '▼'}</span>
                </div>

                {open && (
                  <div style={{ padding: '0 12px 14px 30px' }}>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        backgroundColor: theme.background,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '8px',
                        padding: '12px',
                        color: theme.text,
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        maxHeight: '420px',
                        overflowY: 'auto',
                        margin: 0,
                      }}
                    >
                      {m.body_text || '(no plain-text body)'}
                    </pre>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {m.reply_draft_id ? (
                        <Link to={`/approval-queue?draft=${m.reply_draft_id}`} style={ghostBtn(false)}>
                          Review AI-drafted reply →
                        </Link>
                      ) : (
                        <button
                          onClick={() => doDraftReply(m)}
                          disabled={draftingReplyId === m.id}
                          style={ghostBtn(false)}
                        >
                          {draftingReplyId === m.id ? 'Drafting…' : 'Draft AI reply'}
                        </button>
                      )}
                      <span style={{ color: theme.textSecondary, fontSize: '11px' }}>
                        Never sent automatically. Files into your Approval Queue for review.
                      </span>
                    </div>
                    {draftReplyError && (
                      <p style={{ color: theme.error, fontSize: '12px', marginTop: '6px' }}>{draftReplyError}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!unseenOnly && messages.length < totalCount && (
        <div style={{ textAlign: 'center', marginTop: '14px' }}>
          <button onClick={loadMore} disabled={loadingMore} style={ghostBtn(false)}>
            {loadingMore ? 'Loading…' : `Load more (${messages.length} of ${totalCount})`}
          </button>
        </div>
      )}
      </>
      )}

      {tab === 'quest' && (
      <>
      {/* Signal strip */}
      <div style={{ ...cardStyle, marginTop: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div>
            <div style={{ color: theme.text, fontSize: '36px', fontWeight: 700, lineHeight: 1 }}>
              {questSummary ? questSummary.unseen : '-'}
            </div>
            <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '4px' }}>
              unseen ask{questSummary?.unseen === 1 ? '' : 's'}
            </div>
          </div>
          <div style={{ color: theme.textSecondary, fontSize: '12px' }}>
            {questTotal} ask{questTotal === 1 ? '' : 's'} at this filter
          </div>
        </div>

        <div style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>
          Asks by quest
        </div>
        {questFilterEntries.length === 0 ? (
          <div style={{ color: theme.textSecondary, fontSize: '13px' }}>No Quest asks synced yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {questFilterEntries.map(([questId, counts]) => (
              <button
                key={questId}
                onClick={() => setQuestIdFilter((v) => (v === questId ? '' : questId))}
                title="Filter this list to this quest"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: `1px solid ${questIdFilter === questId ? theme.primary : theme.border}`,
                  backgroundColor: counts.unseen > 0 ? theme.background : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                }}
              >
                <span style={{ color: theme.text, fontSize: '13px', flex: '1 1 auto', fontWeight: counts.unseen > 0 ? 700 : 400 }}>
                  {counts.title}
                </span>
                {counts.unseen > 0 && (
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#FFFFFF',
                      backgroundColor: theme.error,
                      borderRadius: '999px',
                      padding: '1px 8px',
                    }}
                  >
                    {counts.unseen} new
                  </span>
                )}
                <span style={{ color: theme.textSecondary, fontSize: '12px' }}>{counts.total} total</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ ...cardStyle, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ color: theme.textSecondary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Quest
          <select value={questIdFilter} onChange={(e) => setQuestIdFilter(e.target.value)} style={selectStyle}>
            <option value="">All quests</option>
            {questFilterEntries.map(([questId, counts]) => (
              <option key={questId} value={questId}>
                {counts.title}
              </option>
            ))}
          </select>
        </label>

        <label style={{ color: theme.textSecondary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          State
          <select value={questStateFilter} onChange={(e) => setQuestStateFilter(e.target.value)} style={selectStyle}>
            <option value="">All states</option>
            {QUEST_ASK_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <button onClick={() => setStillOwedOnly((v) => !v)} style={ghostBtn(stillOwedOnly)}>
          Still owed only
        </button>
        <button onClick={() => setQuestUnseenOnly((v) => !v)} style={ghostBtn(questUnseenOnly)}>
          Unseen only
        </button>

        <button onClick={doQuestSync} disabled={questSyncing} style={{ ...btn(theme.primary), marginLeft: 'auto' }}>
          {questSyncing ? 'Syncing…' : 'Sync now'}
        </button>
        <button
          onClick={doMarkAllQuestSeen}
          disabled={questMarkingAll || (questSummary?.unseen ?? 0) === 0}
          style={ghostBtn(false)}
        >
          {questMarkingAll ? 'Marking…' : 'Mark all as seen'}
        </button>
      </div>

      {questSyncResult && <div style={{ ...cardStyle, color: theme.text, fontSize: '13px' }}>{questSyncResult}</div>}
      {questBanner && <div style={{ ...cardStyle, color: theme.error, fontSize: '13px' }}>{questBanner}</div>}

      {/* Ask list */}
      {loadingQuestList && questAsks.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>Loading…</div>
      ) : questAsks.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>
          Nothing at this filter.
        </div>
      ) : (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', overflow: 'hidden' }}>
          {questAsks.map((a, i) => {
            const unseen = !a.seen_at;
            const open = expandedQuestKey === a.key;
            return (
              <div
                key={a.key}
                style={{
                  borderTop: i === 0 ? 'none' : `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                }}
              >
                <div
                  onClick={() => toggleExpandQuest(a)}
                  title="Click to view the full ask"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '12px',
                    cursor: 'pointer',
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    title={unseen ? 'Unseen' : 'Seen'}
                    style={{
                      marginTop: '5px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '999px',
                      backgroundColor: unseen ? theme.error : theme.border,
                      flex: '0 0 auto',
                    }}
                  />
                  <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <a
                        href={a.quest_link}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: theme.info,
                          border: `1px solid ${theme.info}`,
                          borderRadius: '999px',
                          padding: '1px 8px',
                          whiteSpace: 'nowrap',
                          textDecoration: 'none',
                        }}
                      >
                        {a.quest_title}
                      </a>
                      <span style={{ color: theme.text, fontSize: '13px', fontWeight: unseen ? 700 : 400 }}>
                        {a.author}
                      </span>
                      <span style={{ color: theme.textSecondary, fontSize: '12px', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                        {fmtTime(a.occurred_at)}
                      </span>
                    </div>
                    <div
                      style={{
                        color: theme.text,
                        fontSize: '13px',
                        fontWeight: unseen ? 700 : 400,
                        marginTop: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {a.text || '(no text)'}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={kindChipStyle}>{kindLabel(a.kind)}</span>
                      <span style={stateChipStyle(a.still_owed)}>{a.state}</span>
                      {a.still_owed && <span style={owedChipStyle}>still owed</span>}
                    </div>
                  </div>
                  <span style={{ color: theme.textSecondary, fontSize: '12px' }}>{open ? '▲' : '▼'}</span>
                </div>

                {open && (
                  <div style={{ padding: '0 12px 14px 30px' }}>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        backgroundColor: theme.background,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '8px',
                        padding: '12px',
                        color: theme.text,
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        maxHeight: '420px',
                        overflowY: 'auto',
                        margin: 0,
                      }}
                    >
                      {a.text || '(no text)'}
                    </pre>
                    {a.cc.length > 0 && (
                      <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '8px' }}>
                        cc: {a.cc.join(', ')}
                      </div>
                    )}
                    {a.last_note && (
                      <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '4px' }}>
                        <strong style={{ color: theme.text }}>Last note:</strong> {a.last_note}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                      <label style={{ color: theme.textSecondary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Set state
                        <select
                          value={stateDraft[a.key] ?? a.state}
                          onChange={(e) => setStateDraft((prev) => ({ ...prev, [a.key]: e.target.value }))}
                          style={selectStyle}
                        >
                          {QUEST_ASK_STATES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </label>
                      <input
                        type="text"
                        placeholder="Optional note"
                        value={noteDraft[a.key] ?? ''}
                        onChange={(e) => setNoteDraft((prev) => ({ ...prev, [a.key]: e.target.value }))}
                        style={{ ...selectStyle, flex: '1 1 200px' }}
                      />
                      <button onClick={() => doUpdateAskState(a)} disabled={updatingKey === a.key} style={btn(theme.primary)}>
                        {updatingKey === a.key ? 'Updating…' : 'Update state'}
                      </button>
                    </div>
                    {updateError && <p style={{ color: theme.error, fontSize: '12px', marginTop: '6px' }}>{updateError}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {questAsks.length < questTotal && (
        <div style={{ textAlign: 'center', marginTop: '14px' }}>
          <button onClick={loadMoreQuest} disabled={loadingMoreQuest} style={ghostBtn(false)}>
            {loadingMoreQuest ? 'Loading…' : `Load more (${questAsks.length} of ${questTotal})`}
          </button>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Responses;
